import { CodeBlock, SectionTitle, SubTitle, InfoBox, Badge } from './shared';

export default function AgentContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">智能体</h1>
        <p className="text-slate-400 text-base leading-relaxed">
          智能体运行时介绍，包括工作区、引导文件注入、内置工具、Skills 系统、会话、模型引用与配置。
        </p>
      </div>

      <SectionTitle id="agent-runtime">智能体运行时</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        每个智能体在独立的运行时环境中执行，拥有自己的工作区、会话和工具集。智能体通过 Gateway 接收消息，经过智能体循环处理后返回回复。
      </p>
      <InfoBox type="info">
        智能体运行时是无状态的——所有持久化状态都存储在工作区文件和会话中，这使得智能体可以安全地重启和迁移。
      </InfoBox>

      <SectionTitle id="agent-workspace">工作区</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每个智能体都有一个专属工作区目录，用于存储配置、记忆文件、技能脚本等。默认位置为：
      </p>
      <CodeBlock language="bash" code={`~/.openclaw/agents/<agent-id>/`} />
      <p className="text-slate-300 text-sm leading-relaxed mb-3">工作区目录结构：</p>
      <CodeBlock language="bash" code={`~/.openclaw/agents/<agent-id>/
├── agent.json5          # 智能体配置文件
├── memory.md            # 记忆文件（Markdown）
├── skills/              # 技能脚本目录
│   ├── search.ts
│   └── calculator.ts
├── sessions/            # 会话存储目录
└── .git/                # Git 备份（可选）`} />

      <SectionTitle id="agent-bootstrap">引导文件注入</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        智能体启动时，会自动将以下文件注入到系统提示中：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5">
          <i className="ri-file-text-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">memory.md：</strong>长期记忆文件，包含智能体积累的知识和用户偏好</span>
        </li>
        <li className="flex items-start gap-2.5">
          <i className="ri-file-text-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">agent.json5 中的 systemPrompt：</strong>智能体的角色定义和行为规范</span>
        </li>
        <li className="flex items-start gap-2.5">
          <i className="ri-file-text-line text-cyan-400 mt-0.5 flex-shrink-0"></i>
          <span><strong className="text-white">技能描述：</strong>所有已加载技能的功能描述，让模型知道可以调用哪些工具</span>
        </li>
      </ul>

      <SectionTitle id="agent-tools">内置工具</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        每个智能体默认内置以下工具：
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 my-4">
        {[
          { icon: 'ri-terminal-line', name: 'bash', desc: '执行 shell 命令' },
          { icon: 'ri-file-edit-line', name: 'write_file', desc: '写入文件内容' },
          { icon: 'ri-file-search-line', name: 'read_file', desc: '读取文件内容' },
          { icon: 'ri-search-line', name: 'search_memory', desc: '搜索记忆文件' },
          { icon: 'ri-global-line', name: 'web_search', desc: '网络搜索（需配置）' },
          { icon: 'ri-code-line', name: 'run_code', desc: '执行代码片段' },
        ].map((tool) => (
          <div key={tool.name} className="flex items-center gap-3 bg-slate-800/40 border border-slate-700/50 rounded-lg px-3 py-2.5">
            <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
              <i className={`${tool.icon} text-cyan-400`}></i>
            </div>
            <div>
              <code className="text-cyan-400 text-xs font-mono">{tool.name}</code>
              <div className="text-xs text-slate-400 mt-0.5">{tool.desc}</div>
            </div>
          </div>
        ))}
      </div>

      <SectionTitle id="agent-skills">Skills 系统</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        Skills（技能）是可插拔的工具模块，可以扩展智能体的能力。技能以 TypeScript 文件形式存储在工作区的 <code className="text-cyan-400 bg-slate-800/60 px-1 rounded text-xs">skills/</code> 目录中。
      </p>
      <CodeBlock language="typescript" code={`// skills/weather.ts
export const skill = {
  name: "get_weather",
  description: "获取指定城市的当前天气",
  parameters: {
    city: { type: "string", description: "城市名称" }
  },
  async execute({ city }: { city: string }) {
    const res = await fetch(\`https://api.weather.com/v1/\${city}\`);
    return res.json();
  }
};`} />
      <InfoBox type="tip">
        技能文件修改后无需重启智能体，OpenClaw 会自动热重载技能。
      </InfoBox>

      <SectionTitle id="agent-pimono">pi-mono 集成</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        OpenClaw 与 pi-mono 深度集成，支持在智能体中直接调用 pi-mono 的功能模块：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    integrations: {
      piMono: {
        enabled: true,
        // pi-mono 服务地址
        endpoint: "http://localhost:4000",
        // 允许调用的模块
        modules: ["calendar", "contacts", "notes"]
      }
    }
  }
}`} />

      <SectionTitle id="agent-session">会话</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        智能体为每个对话对等方（用户/群组）维护独立的会话，会话包含：
      </p>
      <ul className="space-y-2 text-sm text-slate-300 mb-4">
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>消息历史（最近 N 条）</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>压缩摘要（超出上下文窗口时自动生成）</span></li>
        <li className="flex items-start gap-2.5"><i className="ri-checkbox-circle-line text-cyan-400 mt-0.5 flex-shrink-0"></i><span>会话元数据（渠道来源、用户信息等）</span></li>
      </ul>

      <SectionTitle id="agent-streaming">流式传输中的引导</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        智能体支持流式传输模式，在模型生成回复的同时逐步发送内容。引导（Bootstrap）阶段在流式传输开始前完成，确保系统提示和记忆已正确注入。
      </p>

      <SectionTitle id="agent-model-ref">模型引用</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">
        智能体配置中的模型引用支持多种格式：
      </p>
      <CodeBlock language="json5" code={`{
  agent: {
    // 直接指定模型名称
    model: "claude-3-5-sonnet-20241022",
    
    // 或使用提供商前缀
    model: "anthropic/claude-3-5-sonnet",
    
    // 或使用 OpenRouter
    model: "openrouter/anthropic/claude-3.5-sonnet",
    
    // 或使用本地模型
    model: "ollama/llama3.2",
  }
}`} />

      <SectionTitle id="agent-config">配置</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-3">完整的智能体配置示例：</p>
      <CodeBlock language="json5" code={`{
  agent: {
    // 智能体名称（显示用）
    name: "我的助手",
    
    // 使用的 AI 模型
    model: "claude-3-5-sonnet-20241022",
    
    // 系统提示词
    systemPrompt: "你是一个有帮助的 AI 助手。",
    
    // 记忆配置
    memory: {
      enabled: true,
      // 记忆文件路径（相对于工作区）
      filePath: "memory.md",
    },
    
    // 技能配置
    skills: {
      // 技能目录
      dir: "skills/",
      // 禁用的内置工具
      disabledBuiltins: [],
    },
    
    // 会话配置
    session: {
      // 最大消息数（超出后触发压缩）
      maxMessages: 50,
      // 压缩策略
      compactionStrategy: "auto",
    },
    
    // 流式传输配置
    streaming: {
      enabled: true,
      // 分块大小（字符数）
      chunkSize: 100,
    }
  }
}`} />
    </div>
  );
}
